import React, { FC } from 'react';

import { Button, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { Field } from 'client/blocks/form/Field/Field';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import { getIsAdmin } from 'client/utils';
import * as keyset from 'i18n/AliceExternalAppForm';

import styles from './AliceExternalAdminSettings.module.scss';
import { useMcpTimeout } from './useMcpTimeout';

const t = i18n(keyset);

interface Props {
    app: AliceAgentAppDTO;
    onSaved: () => Promise<unknown> | void;
}

/**
 * Admin-only settings that are non-versioned (apply to draft and published at once).
 * Currently hosts the MCP request-timeout override; future admin-only fields land here too.
 */
export const AliceExternalAdminSettings: FC<Props> = ({ app, onSaved }) => {
    const { value, onUpdate, valid, isDirty, isSaving, handleSave } = useMcpTimeout({
        skillId: app.skillId,
        mcpTimeoutSeconds: app.mcpTimeoutSeconds,
        onSaved,
    });

    // Sidebar hides this tab from non-admins; guard direct-URL access too.
    if (!getIsAdmin()) return null;

    return (
        <ErrorBoundary withHomepageLink>
            <div className={styles.page}>
                <h2 className={styles.pageHeader}>{t('side-menu-admin-settings')}</h2>
                <div className={styles.section}>
                    <Field
                        contentClassName={styles.field}
                        fieldTitle={t('field-mcp-timeout')}
                        fieldDescription={t('field-mcp-timeout-description')}
                        error={!valid ? t('validation-mcp-timeout-range') : undefined}
                    >
                        <TextInput
                            className={styles.fieldInput}
                            value={value}
                            onUpdate={onUpdate}
                            type="number"
                            placeholder={t('field-mcp-timeout-placeholder')}
                            size="l"
                            validationState={!valid ? 'invalid' : undefined}
                        />
                        <Button
                            view="action"
                            size="l"
                            onClick={() => void handleSave()}
                            disabled={!isDirty || !valid || isSaving}
                            loading={isSaving}
                        >
                            {t('action-save')}
                        </Button>
                    </Field>
                </div>
            </div>
        </ErrorBoundary>
    );
};
